/*
 * Ported from the `widget` repo (`src/app/services/custom-css.util.ts`) — logic is generic (host
 * element name is a parameter), so keep the two in sync: a rewrite bug fixed here must be fixed
 * there too, or partner CSS behaves differently between the products.
 *
 * `rewriteCustomCssForDocumentOverlay` (the `scopeAnchor` mode) is smart-widget-only so far — port
 * it too when the widget repo stops appending the raw partner sheet to `document.body`.
 * PORT BACK: a child combinator in front of the element tag (`body > calenso-booking`) is now honoured
 * through `hostAncestorMatches(compound, 'parent')` - stripped blindly, it made a rule that was dead on
 * a nested embed a live `:host` rule (Kanton Aargau's `min-height: 100vh` on the element, 9.2.7).
 * PORT BACK: the four scanners now treat a backslash OUTSIDE a string as an escape (9.2.7). Before,
 * `.a\"` or Tailwind's `.content-\[\'x\'\]` opened a phantom string and the rest of the sheet was
 * copied verbatim — harmless-but-wrong in the shadow copy (those rules stayed unrewritten), a page
 * reach in the document copy.
 */
/**
 * Prepares a partner's custom CSS (css_overwrites) for adoption INSIDE the widget's shadow root.
 *
 * Inside a shadow tree the selectors partners anchor on the document never match — `:root`, `html`,
 * `body`, the custom element, and host-page wrappers all sit at or above the host. Those leading
 * compounds are re-targeted to the shadow host, preserving SPECIFICITY (not just meaning), because
 * the partner sheet is last in the cascade and must keep winning and losing the same fights:
 *
 *   `:root { … }`                          -> `:host { … }`                                (0,1,0)
 *   `html * { … }`                         -> `:where(:host) *:not(never) { … }`           (0,0,1)
 *   `body .calenso-booking div { … }`      -> `:where(:host) .calenso-booking div:not(never)` (0,1,2)
 *   `calenso-booking .form-control { … }`  -> `:where(:host) .form-control:not(never)`      (0,1,1)
 *   `#booking .calenso-booking h1 { … }`   -> `:where(:host) .calenso-booking h1:not(#never)` (1,1,1)
 *
 * `:where(:host)` weighs nothing and `:not(never)` / `:not(.never)` / `:not(#never)` match
 * everything while contributing back the exact type/class/id point the stripped compound had.
 * A rule whose ONLY compound is document-level becomes `:host`, so its custom properties win the
 * tie against the widget's own `:host` defaults — the proximity they had in the light DOM. Rules
 * nested inside one get `:where(&)` rather than the implicit `&`, keeping their specificity.
 *
 * Wrappers are resolved at runtime via `hostAncestorMatches` (`host.closest(compound)`): if the
 * host really sits inside it, the light DOM matched every widget element through it, so dropping it
 * (padded) is exact; otherwise it is left alone. Only descendant combinators are followed.
 *
 * Everything else is untouched. The un-rewritten original is still appended to the document, so
 * rules targeting surfaces outside the widget (CDK overlays, body, `@font-face`) keep working.
 */
export function rewriteCustomCssForShadowRoot(
  css: string,
  hostElementName: string,
  /** `ancestor`: does the host sit inside an element matching `compound`; `parent`: is the widget ELEMENT's direct parent one. */
  hostAncestorMatches: (compound: string, relation?: 'ancestor' | 'parent') => boolean = () => false,
): string {
  const documentLevel = new Set([':root', 'html', 'body', hostElementName.toLowerCase()]);
  return new ShadowCssRewriter(documentLevel, hostAncestorMatches).rewrite(stripComments(css));
}

/**
 * Prepares the partner's custom CSS for the DOCUMENT copy that accompanies a shadow-rooted widget.
 *
 * That copy exists for two things the shadow tree cannot provide: `@font-face` / `@import` (they
 * only load from the document) and the CDK overlays (datepicker, dialogs) on the occasions
 * `ShadowRootOverlayContainer` has to leave them on `document.body`. It must not do anything else —
 * appended raw, a sheet's `body { … }`, `* { … }`, `a { … }` or `.btn { … }` restyles the
 * integrator's page.
 *
 * So every style rule is scoped under the light-DOM nodes the widget OWNS (`anchorClasses`: the
 * overlay containers, and the slot hosts the payment mounts are projected into), and no ordinary
 * rule can match anything else on the page (`@import` and a nested `&` inside `:not()`/`:has()`/
 * `:is()`/`:where()` still can - deliberate authoring in the partner's own sheet, for the partner's own page):
 *
 *   `body { … }`                 -> `:where(.a, .b):not(never) { … }`            (0,0,1) — inherited props reach the panels
 *   `body .mat-calendar { … }`   -> `:where(.a, .b) .mat-calendar:not(never)`   (0,1,1)
 *   `a { … }` / `.btn { … }`     -> `:where(.a, .b) a` / `:where(.a, .b) .btn`  (unchanged specificity)
 *   `#booking .form-control`     -> `:where(.a, .b) #booking .form-control`      (matches nothing — page wrappers never enclosed the overlays)
 *
 * `:where()` is weightless, so a rule keeps the specificity it had in the light DOM, where all of
 * these matched overlay content through the document. At-rules that carry no selector
 * (`@font-face`, `@import`, `@keyframes`) are copied through; conditional ones are recursed into.
 */
export function rewriteCustomCssForDocumentOverlay(
  css: string,
  hostElementName: string,
  anchorClasses: readonly string[],
): string {
  const documentLevel = new Set([':root', 'html', 'body', hostElementName.toLowerCase()]);
  const anchor = anchorClasses.map(anchorClass => `.${anchorClass}`).join(', ');
  return new ShadowCssRewriter(documentLevel, () => false, anchor).rewrite(stripComments(css));
}

/**
 * Prepares the widget's OWN global stylesheet (the `*_webcomponent` `styles.css`) for the integrator's
 * document - run at build time by `extend.webpack.config.js`.
 *
 * That sheet is what `angular.json` `styles[]` emits for the Web Component build: the Material theme,
 * the intl-tel-input / angular-calendar / swiper sheets, the fonts, and the element-scoped Bootstrap +
 * widget base. Linked into the host document by the embed, its class-namespaced rules (`.swiper-*`,
 * `.mat-*`, `.iti__*`, `.cal-*`), its `@keyframes` (`spin`, `loading`, ...) and its `html { --mat-* }`
 * tokens reached the WHOLE page - a carousel, a spinner or an Angular Material app on the host page was
 * restyled by us. In shadow mode the widget itself needs none of it (the shadow host bundles its own
 * copy); the document only has to keep what renders in the light DOM: the CDK overlays on `document.body`
 * (marked `.cbsw-overlay-container`), the payment slot hosts, and - for the `light-dom` opt-out - the
 * widget content inside the element itself.
 *
 * So it gets the partner-sheet treatment: every style rule is scoped under those widget-owned nodes
 * (`anchorClasses` + the element tags), a bare `html` / `:root` / `body` rule becomes the anchors
 * themselves keeping only inherited + custom properties (the `--mat-*` tokens still reach the shadow root
 * and the overlays by inheritance), a rule that already starts with an element tag is copied unchanged,
 * `@font-face` passes through, and every `@keyframes` is renamed with `keyframesPrefix` together with its
 * `animation` / `animation-name` references, so no host animation of the same name is redefined.
 */
export function scopeGlobalStylesheetForDocument(
  css: string,
  hostElementNames: readonly string[],
  anchorClasses: readonly string[],
  keyframesPrefix: string,
): string {
  const tags = hostElementNames.map(name => name.toLowerCase());
  const documentLevel = new Set([':root', 'html', 'body']);
  const anchor = [...anchorClasses.map(anchorClass => `.${anchorClass}`), ...tags].join(', ');
  const scoped = new ShadowCssRewriter(documentLevel, () => false, anchor, new Set(tags)).rewrite(stripComments(css));
  return prefixKeyframes(scoped, keyframesPrefix);
}

/**
 * `<selector> { --a: 1; --b: 2 }` for the custom properties of every bare `html` / `:root` rule in the
 * sheet, conditional at-rules preserved; everything else dropped.
 *
 * The shadow host uses it at build time (extend.webpack.config.js) to carry the Material / intl-tel-input /
 * swiper theme tokens INSIDE the shadow root as `:host { … }`: the shadow bundle's copies of those sheets
 * declare them on `html`, which matches nothing in a shadow tree, and the document sheet's anchor copy
 * cannot reach an element a platform mounts inside a shadow root of its own (the Kanton Aargau "isolated"
 * Manifest Integration). Exporting them from the document root instead (`:where(:root)`) was tried and
 * rejected: 900 unlayered custom properties on the host's `<html>` restyle an Angular Material M3 host,
 * whose components read `var(--mat-button-filled-container-color, var(--mat-sys-primary))`, and beat any
 * host definition made inside `@layer`.
 */
export function extractRootCustomProperties(css: string, selector: string): string {
  return extractRootCustomPropertiesFrom(stripComments(css), selector);
}

function extractRootCustomPropertiesFrom(css: string, selector: string): string {
  let out = '';
  let i = 0;
  while (i < css.length) {
    const boundary = findStatementBoundary(css, i);
    if (boundary.index === -1) {
      break;
    }
    if (boundary.char !== '{') {
      i = boundary.index + 1;
      continue;
    }
    const blockEnd = findMatchingBrace(css, boundary.index);
    const prelude = css.slice(i, boundary.index).trim();
    const body = css.slice(boundary.index + 1, blockEnd);
    if (CONDITIONAL_AT_RULES.test(prelude)) {
      const inner = extractRootCustomPropertiesFrom(body, selector);
      if (inner !== '') {
        out += `${prelude}{${inner}}`;
      }
    } else if (!prelude.startsWith('@') && splitTopLevel(prelude, ',').every(alternative => /^(html|:root)$/i.test(alternative.trim()))) {
      const tokens = splitTopLevel(body, ';').map(declaration => declaration.trim()).filter(declaration => declaration.startsWith('--'));
      if (tokens.length > 0) {
        out += `${selector}{${tokens.join(';')}}`;
      }
    }
    i = blockEnd + 1;
  }
  return out;
}

/**
 * Turns every relative `url()` in the sheet into an absolute one under `base`.
 *
 * A relative URL inside a custom property is resolved where the `var()` is USED, not where the
 * property is declared. The Web Component declares `--iti-path-flags-1x: url(flags.<hash>.webp)` in
 * its document stylesheet and uses it from the shadow bundle - a `<style>` with no stylesheet URL - so
 * the browser resolved it against the integrator's page (`/de/themen/.../flags.<hash>.webp` -> 404: no
 * country flags in the phone field on every shadow-mode embed). `data:`, `#`, protocol and root-relative
 * URLs are left alone.
 */
export function absolutizeRelativeUrls(css: string, base: string): string {
  const root = base.endsWith('/') ? base : base + '/';
  return css.replace(/url\(\s*(['"]?)([^'")\s]+)\1\s*\)/g, (whole, quote, target) => {
    if (/^(?:[a-z][a-z0-9+.-]*:|\/|#)/i.test(target)) {
      return whole;
    }
    return `url(${quote}${root}${target}${quote})`;
  });
}

/**
 * Renames every `@keyframes` (and `@-webkit-keyframes`) the sheet defines to `<prefix><name>` and rewrites
 * the `animation` / `animation-name` declarations that reference them. Names the sheet does not define
 * are left alone (they resolve elsewhere, e.g. a component style inside the shadow root).
 */
export function prefixKeyframes(css: string, prefix: string): string {
  const names = new Set<string>();
  for (const match of css.matchAll(/@(?:-webkit-)?keyframes\s+([^\s{]+)/g)) {
    names.add(match[1]);
  }
  if (names.size === 0) {
    return css;
  }
  const renamedDefinitions = css.replace(/(@(?:-webkit-)?keyframes\s+)([^\s{]+)/g, (whole, head, name) => names.has(name) ? head + prefix + name : whole);
  return rewriteDeclarations(renamedDefinitions, (property, value) => {
    // Bootstrap 5 names its keyframes through custom properties (`--bs-spinner-animation-name: spinner-border`,
    // then `animation: ... var(--bs-spinner-animation-name)`), so those values are rewritten too.
    if (property !== 'animation' && property !== 'animation-name' && property !== '-webkit-animation' && property !== '-webkit-animation-name' && !property.startsWith('--')) {
      return null;
    }
    // each comma-separated animation: the keyframes name is the one whitespace-separated token that is a defined name
    return splitTopLevel(value, ',').map(single => single.replace(/(^|\s)([^\s,]+)(?=\s|$)/g, (whole, space, token) => names.has(token) ? space + prefix + token : whole)).join(',');
  });
}

/** Visits every declaration outside `@font-face` / `@keyframes` bodies; `rewrite` returns the new value or null to keep it. */
function rewriteDeclarations(css: string, rewrite: (property: string, value: string) => string | null): string {
  let out = '';
  let i = 0;
  const declaration = (text: string): string => {
    const colon = text.indexOf(':');
    if (colon === -1) {
      return text;
    }
    const property = text.slice(0, colon).trim().toLowerCase();
    const value = text.slice(colon + 1);
    const replacement = rewrite(property, value);
    return replacement === null ? text : text.slice(0, colon + 1) + replacement;
  };
  while (i < css.length) {
    const boundary = findStatementBoundary(css, i);
    if (boundary.index === -1) {
      out += declaration(css.slice(i));
      break;
    }
    const head = css.slice(i, boundary.index);
    if (boundary.char !== '{') {
      out += (boundary.char === ';' ? declaration(head) : head) + boundary.char;
      i = boundary.index + 1;
      continue;
    }
    const blockEnd = findMatchingBrace(css, boundary.index);
    const body = css.slice(boundary.index + 1, blockEnd);
    const prelude = head.trim();
    const opaque = /^@(?:-webkit-)?(?:font-face|keyframes)\b/i.test(prelude) || (prelude.startsWith('@') && !CONDITIONAL_AT_RULES.test(prelude));
    out += head + '{' + (opaque ? body : rewriteDeclarations(body, rewrite)) + '}';
    i = blockEnd + 1;
  }
  return out;
}

/** `calenso-smart-widget.foo:hover` -> `calenso-smart-widget`; a compound that starts with no type name -> ''. */
function leadingTypeName(compound: string): string {
  const match = /^[a-zA-Z][\w-]*/.exec(compound);
  return match === null ? '' : match[0].toLowerCase();
}

/**
 * Appended to every font family a partner sheet declares with `@font-face`, so the faces the DOCUMENT
 * copy has to carry (a shadow tree cannot load fonts) never join a family the integrator's page uses.
 */
export const PRIVATE_FONT_FAMILY_SUFFIX = ' cbsw';

/**
 * Makes the `@font-face` families a partner sheet declares private to the widget.
 *
 * Fonts only load from the document, so the shadow-rooted widget's document copy keeps every
 * `@font-face` — and a face declared under a family name the host page ALSO uses joins that family:
 * declared later, it wins the descriptor match for the page's own text (measured on bkb.ch, 9.2.7:
 * the partner sheet's `"Neue Haas Unica"` weight-range faces replaced the bank's own, and the one
 * that never loaded pushed its semibold text onto the fallback font).
 *
 * Every `@font-face` family is therefore renamed `"<family> cbsw"`, and every reference to it — a
 * `font-family` / `font` declaration, or a custom property carrying it — lists the private family
 * first and the original after it, so the widget renders exactly as before (the same files, under
 * another name) and still falls back to the page's copy should ours not load. Families the sheet
 * only references (`font-family: Lato`) are untouched. Meant for the shadow-DOM path only; the light
 * DOM / iframe copy stays raw.
 */
export function privatizePartnerFontFaces(css: string): string {
  // comment-stripped, like every other pass: a banner comment before the first block (bkb.ch opens
  // with a 60-line one) would otherwise become part of that block's prelude and hide the `@font-face`
  const stripped = stripComments(css);
  const declared = collectFontFaceFamilies(stripped);
  // Only a family the sheet itself USES is made private. A face declared under a name the sheet never
  // references is meant for something outside it — typically the widget's own default font, re-declared
  // to swap its files — and renaming it would silently drop those files; that shape keeps the name
  // (and the pre-9.2.7 reach) on purpose.
  const families = declared.filter(family => referencesFamily(stripped, family));
  if (families.length === 0) {
    return css;
  }
  return rewriteFontFamilies(stripped, families);
}

/** Does any `font-family` / `font` / custom-property declaration OUTSIDE the face blocks list this family? */
function referencesFamily(css: string, family: string): boolean {
  const probe = rewriteFontFamilies(css, [family]);
  const references = probe.split(`${PRIVATE_FONT_FAMILY_SUFFIX}", `).length - 1;   // one alias insertion per reference
  return references > 0;
}

/** The normalized (unquoted, whitespace-collapsed, lower-cased) `font-family` of every `@font-face` in the sheet. */
function collectFontFaceFamilies(css: string): string[] {
  const families = new Set<string>();
  walkBlocks(stripComments(css), (prelude, body) => {
    if (/^@font-face$/i.test(prelude)) {
      for (const declaration of splitTopLevel(body, ';')) {
        const family = fontFamilyDescriptor(declaration);
        if (family !== null) {
          families.add(normalizeFontFamily(family));
        }
      }
    }
  });
  return [...families];
}

/** `font-family: X` inside an `@font-face` block → `X` (raw, still quoted if it was); anything else → null. */
function fontFamilyDescriptor(declaration: string): string | null {
  const match = /^\s*font-family\s*:\s*([\s\S]*?)\s*(?:!important\s*)?$/i.exec(declaration);
  return match === null || match[1] === '' ? null : match[1];
}

function normalizeFontFamily(family: string): string {
  return family.trim().replace(/^(["'])([\s\S]*)\1$/, '$2').replace(/\s+/g, ' ').trim().toLowerCase();
}

function rewriteFontFamilies(css: string, families: readonly string[]): string {
  let out = '';
  let i = 0;
  while (i < css.length) {
    const boundary = findStatementBoundary(css, i);
    if (boundary.index === -1) {
      out += rewriteFontDeclaration(css.slice(i), families, false);   // the last declaration of a block needs no `;`
      break;
    }
    const head = css.slice(i, boundary.index);
    if (boundary.char !== '{') {
      out += (boundary.char === ';' ? rewriteFontDeclaration(head, families, false) : head) + boundary.char;
      i = boundary.index + 1;
      continue;
    }
    const blockEnd = findMatchingBrace(css, boundary.index);
    const body = css.slice(boundary.index + 1, blockEnd);
    const prelude = head.trim();
    if (/^@font-face$/i.test(prelude)) {
      // the descriptor gets the private name; nothing else in a face block names a family
      out += head + '{' + splitTopLevelKeepingSeparators(body).map(part => rewriteFontDeclaration(part, families, true)).join('') + '}';
    } else if (prelude.startsWith('@') && !CONDITIONAL_AT_RULES.test(prelude)) {
      out += head + '{' + body + '}';   // `@keyframes`, `@page`, `@property`… carry no family references worth touching
    } else {
      out += head + '{' + rewriteFontFamilies(body, families) + '}';
    }
    i = blockEnd + 1;
  }
  return out;
}

/**
 * One declaration. In a face block (`descriptor`), `font-family: X` becomes `font-family: "X cbsw"`.
 * Elsewhere, a `font-family` / `font` / custom-property value that lists a declared family gets the
 * private family inserted in front of it: `"X", serif` → `"X cbsw", "X", serif`.
 */
function rewriteFontDeclaration(declaration: string, families: readonly string[], descriptor: boolean): string {
  if (declaration.endsWith(';')) {
    return rewriteFontDeclaration(declaration.slice(0, -1), families, descriptor) + ';';
  }
  const colon = declaration.indexOf(':');
  if (colon === -1) {
    return declaration;
  }
  const property = declaration.slice(0, colon).trim().toLowerCase();
  const value = declaration.slice(colon + 1);
  if (descriptor) {
    if (property !== 'font-family') {
      return declaration;
    }
    const family = fontFamilyDescriptor(declaration);
    if (family === null || !families.includes(normalizeFontFamily(family))) {
      return declaration;   // a face of a family that stays public keeps its name
    }
    return declaration.slice(0, colon + 1) + value.replace(family, `"${normalizeFontFamilyCase(family)}${PRIVATE_FONT_FAMILY_SUFFIX}"`);
  }
  if (property !== 'font-family' && property !== 'font' && !property.startsWith('--')) {
    return declaration;
  }
  // Linear: the value is split at top-level commas (strings and parentheses respected), each segment
  // is examined once. `font-family` names whole families only; the `font` shorthand and a custom
  // property may carry size/weight words in front of the family, so their family is the longest
  // suffix of words that is a declared family. A `var()` fallback is left alone.
  const segments = splitTopLevel(value, ',');
  const rewritten = segments.map((segment, index) => {
    let body = segment;
    let important = '';
    if (index === segments.length - 1) {
      const bang = /\s*!\s*important\s*$/i.exec(body);
      if (bang !== null) {
        important = body.slice(bang.index);
        body = body.slice(0, bang.index);
      }
    }
    const trimmedEnd = body.replace(/\s+$/, '');
    const trailing = body.slice(trimmedEnd.length);
    const leading = (/^\s*/.exec(trimmedEnd) as RegExpExecArray)[0];
    const token = trimmedEnd.slice(leading.length);
    if (token === '') {
      return segment;
    }
    const alias = (family: string) => `"${normalizeFontFamilyCase(family)}${PRIVATE_FONT_FAMILY_SUFFIX}", `;
    if (token.startsWith('"') || token.startsWith('\'')) {
      return families.includes(normalizeFontFamily(token)) ? leading + alias(token) + token + trailing + important : segment;
    }
    // `font: 700 20px/1.2 "Brand"` / `--brand: 16px 'Brand'`: a quoted family that CLOSES the segment
    const quotedTail = /(["'])(?:\\[\s\S]|(?!\1)[^\\])*\1$/.exec(token);
    if (quotedTail !== null) {
      const quoted = quotedTail[0];
      return families.includes(normalizeFontFamily(quoted)) ? leading + token.slice(0, quotedTail.index) + alias(quoted) + token.slice(quotedTail.index) + trailing + important : segment;
    }
    // The family is the WHOLE remaining name, never a suffix of it (a declared "Sans" must not touch
    // "Open Sans"). In the `font` shorthand - and in a custom property carrying one - the family is
    // everything after the size token (`16px`, `20px/1.2`, `clamp(1rem, 2vw, 2rem)/1.2`, `larger`, ...).
    const words = splitWords(token);
    const sizeAt = property === 'font-family' ? -1 : words.reduce((last, word, index) => isFontSizeToken(word) || word.includes('(') ? index : last, -1);
    const familyWords = words.slice(sizeAt + 1);
    if (familyWords.some(word => /[()"']/.test(word))) {
      return segment;   // `var(--brand-font)`, `local(...)` — not a bare family
    }
    if (familyWords.length === 0 || !families.includes(normalizeFontFamily(familyWords.join(' ')))) {
      return segment;
    }
    // located with whatever whitespace the author put between its words
    const match = new RegExp(familyWords.map(escapeRegExp).join('\\s+') + '$').exec(token);
    if (match === null) {
      return segment;
    }
    return leading + token.slice(0, match.index) + alias(familyWords.join(' ')) + token.slice(match.index) + trailing + important;
  });
  return declaration.slice(0, colon + 1) + rewritten.join(',');
}

/** Splits on whitespace outside parentheses and strings: `clamp(1rem, 2vw, 2rem)/1.2 Neue Haas` -> 3 words. */
function splitWords(text: string): string[] {
  const words: string[] = [];
  let current = '';
  let depth = 0;
  let quote: string | null = null;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quote) {
      current += char;
      if (char === '\\') {
        current += text[++i] ?? '';
      } else if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === '"' || char === '\'') {
      quote = char;
    } else if (char === '(') {
      depth++;
    } else if (char === ')') {
      depth = Math.max(0, depth - 1);
    } else if (depth === 0 && /\s/.test(char)) {
      if (current !== '') {
        words.push(current);
        current = '';
      }
      continue;
    }
    current += char;
  }
  if (current !== '') {
    words.push(current);
  }
  return words;
}

/** `16px`, `1.2em`, `20px/1.2`, `larger`, `medium` - the token that ends the size part of a `font` shorthand. */
function isFontSizeToken(word: string): boolean {
  return /^(\d|\.\d)/.test(word) || /^(xx-small|x-small|small|medium|large|x-large|xx-large|xxx-large|larger|smaller)(\/|$)/i.test(word);
}

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Unquotes and collapses whitespace but keeps the author's letter case for the private name. */
function normalizeFontFamilyCase(family: string): string {
  return family.trim().replace(/^(["'])([\s\S]*)\1$/, '$2').replace(/\s+/g, ' ').trim();
}

/** Splits a block body at top-level `;` but keeps every separator, so the parts re-join byte-exact. */
function splitTopLevelKeepingSeparators(body: string): string[] {
  const parts: string[] = [];
  let i = 0;
  while (i < body.length) {
    const boundary = findStatementBoundary(body, i);
    if (boundary.index === -1 || boundary.char === '{') {
      parts.push(body.slice(i));
      break;
    }
    parts.push(body.slice(i, boundary.index) + boundary.char);
    i = boundary.index + 1;
  }
  return parts;
}

/** Visits every block (at any depth, conditional at-rules included) as (prelude, body). */
function walkBlocks(css: string, visit: (prelude: string, body: string) => void): void {
  let i = 0;
  while (i < css.length) {
    const boundary = findStatementBoundary(css, i);
    if (boundary.index === -1) {
      break;
    }
    if (boundary.char !== '{') {
      i = boundary.index + 1;
      continue;
    }
    const blockEnd = findMatchingBrace(css, boundary.index);
    const prelude = css.slice(i, boundary.index).trim();
    const body = css.slice(boundary.index + 1, blockEnd);
    visit(prelude, body);
    if (CONDITIONAL_AT_RULES.test(prelude) || !prelude.startsWith('@')) {
      walkBlocks(body, visit);
    }
    i = blockEnd + 1;
  }
}

/** Selectors that never match, so `:not(…)` of them is true for every element and worth exactly one point of its tier. */
const NEVER = { ids: '#never', classes: '.never', types: 'never' };
type Specificity = { ids: number; classes: number; types: number };
const CONDITIONAL_AT_RULES = /^@(media|supports|layer|container|scope|starting-style|document|-moz-document)\b/i;

interface RewriteContext {
  /** Set while emitting the block of a bare document-level rule (`body { … }` → `:host { … }`). */
  nestedPadding: string | null;
  /**
   * Document-overlay mode: the enclosing rule's emitted selector IS (or may be) the anchor itself, so a
   * nested sibling-relative prelude (`~ *`, `& + .x`) would resolve against the anchor's siblings —
   * the integrator's own `body` children.
   */
  parentIsAnchor: boolean;
  /**
   * Document-overlay mode: the enclosing rule was a bare document-level one (`body { … }`), now
   * targeting the widget's containers themselves. Only what `body` could pass DOWN to overlay content
   * in the light DOM may be kept - inherited properties and custom properties. A box property
   * (`background`, `min-height`, `display`…) would land on a `position: fixed` full-viewport overlay
   * container and paint over the integrator's page.
   */
  inheritableOnly: boolean;
  /** Set while emitting the block of any style rule: nested preludes are relative to their parent, never to the page. */
  insideStyleRule: boolean;
  /** Block nesting depth — beyond MAX_DEPTH the block is copied verbatim instead of recursed into. */
  depth: number;
}

/** Real partner CSS nests a handful of levels; anything deeper is copied through untouched (bounded recursion, bounded time). */
const MAX_DEPTH = 32;

class ShadowCssRewriter {
  /** `^(:root|html|body|<host tag>)` followed by a class / id / attribute / pseudo — see isDocumentLevelWithModifier. */
  private readonly documentLevelWithModifier: RegExp;

  /**
   * @param scopeAnchor when set (document-overlay mode), every top-level style rule is scoped under
   *   this compound instead of being re-targeted to `:host`; see rewriteCustomCssForDocumentOverlay.
   */
  constructor(
    private readonly documentLevel: Set<string>,
    private readonly hostAncestorMatches: (compound: string, relation?: 'ancestor' | 'parent') => boolean,
    private readonly scopeAnchor: string | null = null,
    /** Document-overlay mode: a top-level selector whose FIRST compound is one of these type names is already scoped and is copied unchanged. */
    private readonly passThroughLeading: ReadonlySet<string> = new Set(),
  ) {
    const names = [...documentLevel].map(name => name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
    this.documentLevelWithModifier = new RegExp(`^(?:${names})(?=[.#\\[:])`);
  }

  rewrite(css: string): string {
    return this.rewriteBlockBody(css, { nestedPadding: null, insideStyleRule: false, depth: 0, parentIsAnchor: false, inheritableOnly: false });
  }

  /** Rewrites a run of declarations / rules (the inside of a block, or the top level). */
  private rewriteBlockBody(css: string, context: RewriteContext): string {
    if (context.depth > MAX_DEPTH) {
      return css;
    }
    let out = '';
    let i = 0;
    while (i < css.length) {
      const boundary = findStatementBoundary(css, i);
      if (boundary.index === -1) {
        // the last declaration of a block needs no `;` - a minified `body{background:#fff}` ends here
        const tail = css.slice(i);
        if (!(context.inheritableOnly && tail.includes(':') && !isInheritableDeclaration(tail))) {
          out += tail;
        }
        break;
      }
      const head = css.slice(i, boundary.index);
      if (boundary.char !== '{') {
        // declaration, `@import …;`, or a stray `}` — copied verbatim, unless the enclosing rule now
        // targets the widget's containers themselves and this declaration could not have reached
        // overlay content from `body` in the light DOM (see RewriteContext.inheritableOnly)
        if (!(context.inheritableOnly && boundary.char === ';' && !isInheritableDeclaration(head))) {
          out += head + boundary.char;
        }
        i = boundary.index + 1;
        continue;
      }
      const blockEnd = findMatchingBrace(css, boundary.index);
      const body = css.slice(boundary.index + 1, blockEnd);
      const prelude = head.trim();
      if (prelude.startsWith('@')) {
        out += head + '{' + (CONDITIONAL_AT_RULES.test(prelude) ? this.rewriteBlockBody(body, { ...context, depth: context.depth + 1 }) : body) + '}';
      } else {
        const rewritten = this.rewritePrelude(prelude, context);
        const leading = (/^\s*/.exec(head) as RegExpExecArray)[0];
        out += leading + rewritten.prelude + ' {' + this.rewriteBlockBody(body, { nestedPadding: rewritten.nestedPadding, insideStyleRule: true, depth: context.depth + 1, parentIsAnchor: rewritten.anchorIsSubject, inheritableOnly: rewritten.bareDocumentLevel }) + '}';
      }
      i = blockEnd + 1;
    }
    return out;
  }

  private rewritePrelude(prelude: string, context: RewriteContext): { prelude: string; nestedPadding: string | null; anchorIsSubject: boolean; bareDocumentLevel: boolean } {
    const selectors = splitTopLevel(prelude, ',').map(selector => selector.trim()).filter(Boolean);
    const results = selectors.map(selector => this.rewriteComplexSelector(selector, context));
    const nestedPaddings = results.map(result => result.nestedPadding).filter((padding): padding is string => padding !== null);
    return {
      prelude: results.map(result => result.selector).join(', '),
      // rules nested inside this one inherit the padding of the (first) bare document-level selector
      nestedPadding: nestedPaddings.length ? nestedPaddings[0] : null,
      anchorIsSubject: results.some(result => result.anchorIsSubject),
      // a list mixing `body` with a class alternative keeps the filter: the bare half is what paints the page
      bareDocumentLevel: results.some(result => result.bareDocumentLevel === true),
    };
  }

  private rewriteComplexSelector(selector: string, context: RewriteContext): Rewritten {
    const parts = tokenizeComplexSelector(selector);
    if (parts.length === 0) {
      return { selector, nestedPadding: null, anchorIsSubject: false };   // a combinator-only prelude (`> { }`) — invalid CSS, left for the browser to drop
    }
    if (context.nestedPadding !== null) {
      // nested directly inside a bare `body { … }` (now `:host { … }`): `:where(&)` drops the host's
      // class point again and the padding restores the type point(s) the parent used to contribute
      const padded = parts.map((part, index) => index % 2 === 0 ? part.replace(/&/g, ':where(&)') : part);
      padded[padded.length - 1] = padCompound(padded[padded.length - 1], context.nestedPadding);
      const rebuilt = padded.join('');
      return { selector: selector.includes('&') ? rebuilt : ':where(&) ' + rebuilt, nestedPadding: null, anchorIsSubject: false };
    }
    if (!context.insideStyleRule && this.passThroughLeading.size > 0 && this.passThroughLeading.has(leadingTypeName(parts[0]))) {
      return { selector, nestedPadding: null, anchorIsSubject: false };   // `calenso-smart-widget .btn` - scoped by construction
    }
    if (context.insideStyleRule) {
      // relative to the parent rule, not to the page — except, in document-overlay mode, a sibling of a
      // parent that IS the anchor: those siblings are the integrator's own body children
      if (this.scopeAnchor !== null && context.parentIsAnchor && nestedReachesSiblings(parts)) {
        return { selector: '&:not(*)', nestedPadding: null, anchorIsSubject: false };
      }
      return { selector, nestedPadding: null, anchorIsSubject: this.scopeAnchor !== null && context.parentIsAnchor && nestedSubjectIsParent(parts) };
    }
    const compounds = parts.filter((_, index) => index % 2 === 0);
    const combinatorAfter = (index: number) => parts[index * 2 + 1];
    if (this.scopeAnchor !== null) {
      return this.scopeUnderAnchor(parts);
    }
    return { ...this.retargetToHost(parts, compounds, combinatorAfter, selector), anchorIsSubject: false };
  }

  /** Shadow-root mode: re-target the leading document-level compounds to `:host` (unchanged since JB-1811). */
  private retargetToHost(parts: string[], compounds: string[], combinatorAfter: (index: number) => string | undefined, selector: string): { selector: string; nestedPadding: string | null } {
    let stripped = 0;
    let rootPoints = 0;
    const padding: Specificity = { ids: 0, classes: 0, types: 0 };
    while (stripped < compounds.length && this.documentLevel.has(compounds[stripped].toLowerCase())) {
      const compound = compounds[stripped].toLowerCase();
      // `body > calenso-smart-widget`: a CHILD combinator in front of the element tag only ever matched
      // when the element was a direct child of that compound. Stripping it blindly would turn a rule
      // that was dead on the integrator's page (the element sits inside wrappers) into a live `:host`
      // rule - Kanton Aargau's `min-height: 100vh` on the element, 2026-09-03.
      const next = compounds[stripped + 1]?.toLowerCase();
      if (combinatorAfter(stripped) === ' > ' && next !== undefined && next !== ':root' && next !== 'html' && next !== 'body' && this.documentLevel.has(next)
        && !this.hostAncestorMatches(compounds[stripped], 'parent')) {
        return { selector: ':host:not(*)', nestedPadding: null };
      }
      if (compound === ':root') {
        rootPoints++;
      } else {
        padding.types++;
      }
      stripped++;
    }
    if (stripped === compounds.length) {
      if (stripped === 0) {
        return { selector, nestedPadding: null };
      }
      // The whole selector is document-level (`body`, `:root`, `html body`) → the host itself. A bare
      // `html { … }` stays weightless: on the light DOM it sat on the same element as the widget's own
      // `:root { --… }` defaults and lost to them, whereas `body`/`:root`/the element itself won.
      const onlyHtml = compounds.every(compound => compound.toLowerCase() === 'html');
      const nestedPadding = padding.types ? paddingFor({ ...padding, classes: padding.classes + rootPoints }) : null;
      return { selector: onlyHtml ? ':where(:host)' : ':host', nestedPadding };
    }
    // page wrappers above the host: strip while the compound matches an ancestor of the host and is
    // followed by a descendant combinator (a stripped compound must never be the subject)
    while (stripped < compounds.length - 1 && combinatorAfter(stripped) === ' ' && this.isHostAncestor(compounds[stripped])) {
      const specificity = compoundSpecificity(compounds[stripped]);
      padding.ids += specificity.ids;
      padding.classes += specificity.classes;
      padding.types += specificity.types;
      stripped++;
    }
    if (stripped === 0) {
      return { selector, nestedPadding: null };
    }
    // `:host` already carries one class point when a `:root` was stripped; the rest is padded onto the subject compound
    const rest = parts.slice(stripped * 2 - 1); // starts with the combinator that followed the last stripped compound
    rest[rest.length - 1] = padCompound(rest[rest.length - 1], paddingFor({ ...padding, classes: padding.classes + Math.max(0, rootPoints - 1) }));
    return { selector: (rootPoints > 0 ? ':host' : ':where(:host)') + rest.join(''), nestedPadding: null };
  }

  /**
   * Document-overlay mode: a document-level compound CARRYING A MODIFIER — `body.dark`,
   * `html[lang="de"]`, `:root[data-theme]`, `body:hover`, `body::before`, the host tag with a class,
   * or anything on `:host`. Not stripped: kept verbatim as a prefix ahead of the anchor, so the
   * condition it expresses survives (the containers do sit inside `body.dark`), and a pseudo-element
   * or a `:host` rule — which never described overlay content — goes inert instead of landing on the
   * containers themselves.
   */
  private isDocumentLevelWithModifier(compound: string): boolean {
    const lower = compound.toLowerCase();
    return lower.startsWith(':host') || this.documentLevelWithModifier.test(lower);
  }

  /**
   * Document-overlay mode: drop the leading EXACT document-level compounds (their specificity is
   * padded back onto the subject), keep modifier-bearing ones as a literal prefix, and put the rest
   * under `:where(anchor)`, a weightless descendant scope. A selector that was ONLY document-level
   * becomes the anchor itself, so inherited properties still flow into the overlay panels the way
   * `body { … }` fed them in the light DOM. A lone class/id/attribute compound may name a container
   * itself (`.cdk-overlay-container { z-index }` — the classic host-header fix), so it is also emitted
   * as the anchor's own subject. A prefix ending in a pseudo-element (`body::before`) cannot have
   * descendants; that selector is replaced by the valid, never-matching `:where(anchor):not(*)` so it
   * neither styles the containers nor invalidates the rest of its selector list.
   */
  private scopeUnderAnchor(parts: string[]): Rewritten {
    const anchor = `:where(${this.scopeAnchor})`;
    const compounds = parts.filter((_, index) => index % 2 === 0);
    const padding: Specificity = { ids: 0, classes: 0, types: 0 };
    const prefix: string[] = [];
    let leading = 0;
    while (leading < compounds.length) {
      const compound = compounds[leading];
      if (this.documentLevel.has(compound.toLowerCase())) {
        const specificity = compoundSpecificity(compound);
        padding.ids += specificity.ids;
        padding.classes += specificity.classes;
        padding.types += specificity.types;
      } else if (this.isDocumentLevelWithModifier(compound)) {
        if (hasPseudoElement(compound)) {
          return { selector: `${anchor}:not(*)`, nestedPadding: null, anchorIsSubject: false };
        }
        prefix.push(compound);
      } else {
        break;
      }
      leading++;
    }
    const scope = prefix.length ? `${prefix.join(' ')} ${anchor}` : anchor;
    if (leading === compounds.length) {
      return { selector: scope + paddingFor(padding), nestedPadding: null, anchorIsSubject: true, bareDocumentLevel: true };
    }
    // `rest` starts with the combinator that followed the last document-level compound (a descendant
    // combinator when there was none), so `body > .x` keeps its child relationship to the anchor
    // A selector that already STARTS with a combinator (`~ *`, valid inside `@scope (body)`) keeps it
    // as its own first token, so the sibling check below sees it; otherwise a descendant step is implied.
    const startsWithCombinator = leading === 0 && /^ [>+~] $/.test(parts[0]);
    const rest = leading === 0 ? (startsWithCombinator ? parts : [' ', ...parts]) : parts.slice(leading * 2 - 1);
    if (rest[0] === ' + ' || rest[0] === ' ~ ') {
      // `body ~ *` / `html + .x`: the anchor's SIBLINGS are the integrator's own body children. The
      // light DOM matched nothing with these (body/html have no styleable siblings), so inert is exact.
      return { selector: `${anchor}:not(*)`, nestedPadding: null, anchorIsSubject: false };
    }
    const subject = rest[rest.length - 1];
    rest[rest.length - 1] = padCompound(subject, paddingFor(padding));
    const asDescendant = scope + rest.join('');
    if (rest.length === 2 && /[.#\[]/.test(subject)) {
      const asSelf = (prefix.length ? `${prefix.join(' ')} ` : '') + padCompound(subject, paddingFor(padding) + anchor);
      return { selector: `${asDescendant}, ${asSelf}`, nestedPadding: null, anchorIsSubject: true };
    }
    return { selector: asDescendant, nestedPadding: null, anchorIsSubject: false };
  }

  /** Does the host or one of its ancestors match this compound? Never asked for `&`, `:host…` or a lone pseudo-class. */
  private isHostAncestor(compound: string): boolean {
    if (compound === '&' || compound.startsWith(':host') || /^:[\w-]+$/.test(compound)) {
      return false;
    }
    try {
      return this.hostAncestorMatches(compound);
    } catch {
      return false;
    }
  }
}

function paddingFor(specificity: Specificity): string {
  return `:not(${NEVER.ids})`.repeat(specificity.ids) + `:not(${NEVER.classes})`.repeat(specificity.classes) + `:not(${NEVER.types})`.repeat(specificity.types);
}

/** Specificity of one compound selector — enough for the wrappers partners write (`#booking`, `div.page`, `main`). */
function compoundSpecificity(compound: string): Specificity {
  const specificity: Specificity = { ids: 0, classes: 0, types: 0 };
  // functional pseudo-classes: :where() is weightless, :not()/:is()/:has() count their most specific argument
  const rest = compound
    .replace(/:(not|is|has|where)\(((?:[^()]|\([^()]*\))*)\)/g, (_, name: string, args: string) => {
      if (name !== 'where') {
        const best = splitTopLevel(args, ',').map(argument => complexSpecificity(argument.trim()))
          .reduce((max, current) => compare(current, max) > 0 ? current : max, { ids: 0, classes: 0, types: 0 });
        specificity.ids += best.ids;
        specificity.classes += best.classes;
        specificity.types += best.types;
      }
      return '';
    })
    .replace(/\[[^\]]*\]/g, () => { specificity.classes++; return ''; })
    .replace(/::[\w-]+/g, () => { specificity.types++; return ''; });
  specificity.ids += (rest.match(/#[\w-]+/g) ?? []).length;
  specificity.classes += (rest.match(/\.[\w-]+/g) ?? []).length + (rest.match(/:[\w-]+/g) ?? []).length;
  specificity.types += /^-?[A-Za-z_][\w-]*/.test(rest) ? 1 : 0;
  return specificity;
}

function complexSpecificity(selector: string): Specificity {
  return tokenizeComplexSelector(selector).filter((_, index) => index % 2 === 0).map(compoundSpecificity)
    .reduce((sum, current) => ({ ids: sum.ids + current.ids, classes: sum.classes + current.classes, types: sum.types + current.types }), { ids: 0, classes: 0, types: 0 });
}

function compare(a: Specificity, b: Specificity): number {
  return a.ids - b.ids || a.classes - b.classes || a.types - b.types;
}

interface Rewritten {
  selector: string;
  nestedPadding: string | null;
  /** Document-overlay mode: the emitted selector is (or, as an alternative, may be) the anchor itself. */
  anchorIsSubject: boolean;
  /** Document-overlay mode: the selector was ONLY document-level (`body`, `html body`, `body.dark`, `:root`). */
  bareDocumentLevel?: boolean;
}

/**
 * Properties that inherit (plus custom properties): what a light-DOM `body { … }` handed down to the
 * overlay panels and the payment box. Everything else on a bare document rule is a box property that
 * must not be applied to the containers themselves.
 */
const INHERITED_PROPERTIES = new Set([
  'color', 'cursor', 'direction', 'font', 'font-family', 'font-feature-settings', 'font-kerning', 'font-optical-sizing',
  'font-size', 'font-size-adjust', 'font-stretch', 'font-style', 'font-variant', 'font-variant-caps', 'font-variant-ligatures',
  'font-variant-numeric', 'font-weight', 'hyphens', 'letter-spacing', 'line-height', 'list-style', 'list-style-image',
  'list-style-position', 'list-style-type', 'quotes', 'tab-size', 'text-align', 'text-align-last', 'text-indent',
  'text-justify', 'text-rendering', 'text-shadow', 'text-transform', 'text-underline-offset', 'text-underline-position',
  'visibility', 'white-space', 'word-break', 'word-spacing', 'word-wrap', 'overflow-wrap', 'writing-mode', 'caption-side',
  'empty-cells', 'border-collapse', 'border-spacing', 'orphans', 'widows', 'font-smoothing', 'osx-font-smoothing',
  'text-size-adjust', 'accent-color', 'color-scheme', 'print-color-adjust', 'image-rendering',
]);

function isInheritableDeclaration(declaration: string): boolean {
  const colon = declaration.indexOf(':');
  if (colon === -1) {
    return true;   // not a declaration (`@import …`, stray text) — leave it alone
  }
  const property = declaration.slice(0, colon).trim().toLowerCase();
  if (property.startsWith('--')) {
    return true;
  }
  return INHERITED_PROPERTIES.has(property.replace(/^-(webkit|moz|ms|o)-/, ''));
}

const SIBLING = new Set([' + ', ' ~ ']);

/** A nested prelude whose subject is a SIBLING of the parent: `~ *`, `+ .x`, `& ~ .x`, `:is(&) + .x`. */
function nestedReachesSiblings(parts: string[]): boolean {
  if (SIBLING.has(parts[0])) {
    return true;
  }
  for (let index = 0; index + 1 < parts.length; index++) {
    if (parts[index].includes('&') && SIBLING.has(parts[index + 1])) {
      return true;
    }
  }
  return false;
}

/** A nested prelude whose subject is still the parent itself: `&`, `&:hover`, `:hover`, `.x &`. */
function nestedSubjectIsParent(parts: string[]): boolean {
  const last = parts[parts.length - 1];
  return last.includes('&') || (parts.length === 1 && last.startsWith(':'));
}

const PSEUDO_ELEMENT = /::|:(before|after|first-line|first-letter)(?![\w-])/i;

function hasPseudoElement(compound: string): boolean {
  return PSEUDO_ELEMENT.test(compound);
}

/** Appends the padding to the compound, ahead of any pseudo-element (`.x::before` → `.x:not(never)::before`). */
function padCompound(compound: string, padding: string): string {
  if (!padding) {
    return compound;
  }
  const pseudoElement = PSEUDO_ELEMENT.exec(compound);
  const at = pseudoElement ? pseudoElement.index : compound.length;
  return compound.slice(0, at) + padding + compound.slice(at);
}

/** Splits `a > b .c` into `['a', ' > ', 'b', ' ', '.c']` (compounds at even indexes, combinators at odd). */
function tokenizeComplexSelector(selector: string): string[] {
  const parts: string[] = [];
  let compound = '';
  let combinator = '';
  let depth = 0;
  let quote: string | null = null;
  for (let i = 0; i < selector.length; i++) {
    const char = selector[i];
    if (quote) {
      compound += char;
      if (char === '\\') {
        compound += selector[++i] ?? '';
      } else if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === '"' || char === '\'') {
      quote = char;
      compound += char;
      continue;
    }
    if (char === '\\') {
      // an escape OUTSIDE a string (`.a\"`, Tailwind's `.content-\[\'x\'\]`) — the pair belongs to the
      // compound and must not open a phantom string or group
      if (combinator) {
        parts.push(normalizeCombinator(combinator));
        combinator = '';
      }
      compound += char + (selector[++i] ?? '');
      continue;
    }
    if (char === '(' || char === '[') {
      depth++;
    } else if (char === ')' || char === ']') {
      depth--;
    }
    if (depth === 0 && /[\s>+~]/.test(char)) {
      if (compound) {
        parts.push(compound);
        compound = '';
      }
      combinator += char;
      continue;
    }
    if (combinator) {
      parts.push(normalizeCombinator(combinator));
      combinator = '';
    }
    compound += char;
  }
  if (compound) {
    parts.push(compound);
  }
  return parts;
}

function normalizeCombinator(raw: string): string {
  const symbol = raw.replace(/\s+/g, '');
  return symbol ? ` ${symbol[0]} ` : ' ';
}

/** Splits on `separator` outside of parentheses, brackets and strings. */
function splitTopLevel(text: string, separator: string): string[] {
  const parts: string[] = [];
  let current = '';
  let depth = 0;
  let quote: string | null = null;
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (quote) {
      current += char;
      if (char === '\\') {
        current += text[++i] ?? '';
      } else if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === '\\') {
      current += char + (text[++i] ?? '');   // escape outside a string: keep the pair, never a phantom string/group
      continue;
    }
    if (char === '"' || char === '\'') {
      quote = char;
    } else if (char === '(' || char === '[') {
      depth++;
    } else if (char === ')' || char === ']') {
      depth--;
    } else if (char === separator && depth === 0) {
      parts.push(current);
      current = '';
      continue;
    }
    current += char;
  }
  parts.push(current);
  return parts;
}

/** Index and kind of the next top-level `{`, `;` or `}` at or after `from` (-1 if none). */
function findStatementBoundary(css: string, from: number): { index: number; char: string } {
  let depth = 0;
  let quote: string | null = null;
  for (let i = from; i < css.length; i++) {
    const char = css[i];
    if (quote) {
      if (char === '\\') {
        i++;
      } else if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === '\\') {
      i++;   // escape outside a string: skip the escaped char, never a phantom string/group
      continue;
    }
    if (char === '"' || char === '\'') {
      quote = char;
    } else if (char === '(' || char === '[') {
      depth++;
    } else if (char === ')' || char === ']') {
      depth = Math.max(0, depth - 1);
    } else if (depth === 0 && (char === '{' || char === ';' || char === '}')) {
      return { index: i, char };
    }
  }
  return { index: -1, char: '' };
}

/** Index of the `}` closing the `{` at `open` (end of text if unbalanced). */
function findMatchingBrace(css: string, open: number): number {
  let level = 0;
  let quote: string | null = null;
  for (let i = open; i < css.length; i++) {
    const char = css[i];
    if (quote) {
      if (char === '\\') {
        i++;
      } else if (char === quote) {
        quote = null;
      }
      continue;
    }
    if (char === '\\') {
      i++;   // escape outside a string
      continue;
    }
    if (char === '"' || char === '\'') {
      quote = char;
    } else if (char === '{') {
      level++;
    } else if (char === '}') {
      level--;
      if (level === 0) {
        return i;
      }
    }
  }
  return css.length;
}

function stripComments(css: string): string {
  return css.replace(/\/\*[\s\S]*?\*\//g, '');
}
